import styled from '@emotion/styled'
import { Button, Flex, Input, Select, Text, useToastPopup } from '@collable/ui'
import ManageChannelInfoItem from '../ManageInfoItem'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChannelId } from '@/types/common'
import { useGetChannelSettings, usePostChannelAudience, usePutChannelSettings } from '@/api/channel'
import { ContentTypesOptions, GenderOptions, GenerationOptions } from '@/constants/selectOptions'
import AudienceImageStatus from './AudienceImageStatus'
import type { AudienceRequestStatus, GetChannelSettingsResponse } from '@/api/channel/type'
import { emailRegex } from '@/utils/regex'
import type { Category } from '@/api/types'

interface ManageChannelInfoProps {
  channelId: ChannelId
}

const AUDIENCE_REQUEST_STATUS_MAP: Record<Exclude<AudienceRequestStatus, null>, string> = {
  PENDING: '등록한 시청자 데이터를 확인한 후, 승인 여부를 결정할 예정이에요',
  ACCEPTED: '등록한 시청자 데이터가 콜라블에 반영되었어요.',
  REJECTED: '등록한 이미지로는 확인이 어려워 승인이 거부되었어요. 다시 등록해 주세요.',
}

const getInitialState = (channelSettings?: GetChannelSettingsResponse) => {
  return {
    email: channelSettings?.email ?? '',
    contentType: (channelSettings?.category ?? '') as Category,
    mcn: channelSettings?.mcn ?? '',
    heroGender: channelSettings?.heroGender ?? '',
    heroAge: channelSettings?.heroAge?.toString() ?? '',
  }
}

const ManageChannelInfo = ({ channelId }: ManageChannelInfoProps) => {
  const { data: channelSettings } = useGetChannelSettings(channelId)
  const { addToastPopup } = useToastPopup()

  const [audience, setAudience] = useState<{
    file: File | null
    status: AudienceRequestStatus | 'SELECTED' | 'EMPTY'
  }>({
    file: null,
    status: 'EMPTY',
  })
  const [initialState, setInitialState] = useState(getInitialState(channelSettings))
  const [form, setForm] = useState(getInitialState(channelSettings))
  const [errors, setErrors] = useState<string[]>([])

  const { mutate: putChannelSettings } = usePutChannelSettings()
  const { mutate: postChannelAudience } = usePostChannelAudience()

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setInitialState(getInitialState(channelSettings))
    setForm(getInitialState(channelSettings))
    setAudience({
      file: null,
      status: channelSettings?.audienceRequest?.status ?? 'EMPTY',
    })
  }, [channelSettings])

  const isChanged = useMemo(() => {
    return JSON.stringify(initialState) !== JSON.stringify(form) || !!audience.file
  }, [initialState, form, audience.file])

  const saveChannelSettings = (audienceUrl: string) => {
    putChannelSettings(
      {
        channelId,
        payload: {
          email: form.email,
          mcn: form.mcn,
          category: form.contentType,
          audienceUrl,
          heroGender: form.heroGender,
          heroAge: Number(form.heroAge),
          sns: [],
        },
      },
      {
        onSuccess: () => {
          addToastPopup({
            type: 'normal',
            message: '채널 정보를 수정했어요',
            status: 'positive',
          })
        },
      },
    )
  }

  const onClickSave = () => {
    if (!emailRegex.test(form.email)) {
      addToastPopup({
        type: 'normal',
        message: '정확한 이메일 주소를 입력해주세요',
        status: 'danger',
      })
      setErrors(['email'])
      return
    }

    if (audience.file) {
      postChannelAudience(
        {
          channelId,
          file: audience.file,
        },
        {
          onSuccess: res => {
            saveChannelSettings(res.fileUrl)
          },
        },
      )
      return
    }

    saveChannelSettings(channelSettings?.audienceRequest?.imageUrl ?? '')
  }

  return (
    <Flex direction="column" gap={48} alignItems="flex-end">
      <Flex direction="column" gap={12} as="ol" style={{ width: '100%' }}>
        <ManageChannelInfoItem title="1. 비즈니스 메일 주소">
          <Input
            size="large"
            placeholder="메일 주소 입력"
            value={form.email}
            onChange={e => {
              setForm({ ...form, email: e.target.value })
              setErrors(prev => prev.filter(error => error !== 'email'))
            }}
            width="100%"
            isError={errors.includes('email')}
          />
        </ManageChannelInfoItem>
        <ManageChannelInfoItem title="2. 주 콘텐츠">
          <Select
            placeholder="주 콘텐츠"
            size="large"
            options={ContentTypesOptions}
            type="outline"
            width="100%"
            selectedOption={form.contentType}
            onChange={value => setForm({ ...form, contentType: value })}
          />
        </ManageChannelInfoItem>
        <ManageChannelInfoItem title="3. 소속사">
          <Input
            size="large"
            placeholder="소속사 입력"
            value={form.mcn}
            onChange={e => setForm({ ...form, mcn: e.target.value })}
            width="100%"
            maxLength={19}
          />
        </ManageChannelInfoItem>
        <ManageChannelInfoItem title="4. 메인 출연자">
          <CreatorPersonalInfoWrapper gap={8}>
            <Select
              placeholder="성별"
              size="large"
              options={GenderOptions}
              type="outline"
              selectedOption={form.heroGender}
              onChange={value => setForm({ ...form, heroGender: value })}
              width="100%"
            />
            <Select
              placeholder="나이대"
              size="large"
              options={GenerationOptions}
              type="outline"
              selectedOption={form.heroAge}
              onChange={value => setForm({ ...form, heroAge: value })}
              width="100%"
            />
          </CreatorPersonalInfoWrapper>
        </ManageChannelInfoItem>
        <ManageChannelInfoItem
          title="5. 시청자 데이터"
          description={
            audience.status === 'EMPTY' ? (
              <Text size="body3" color="primaryNormal">
                시청자 데이터를 등록하면 광고 의뢰 확률이 눈에 띄게 높아져요
              </Text>
            ) : (
              audience.status !== 'SELECTED' && (
                <Text size="body3" color="labelAlternative">
                  {AUDIENCE_REQUEST_STATUS_MAP[audience.status ?? '']}
                </Text>
              )
            )
          }
          optionComponent={
            audience.status !== 'EMPTY' ? (
              <Button
                kind="outline"
                size="small"
                color="neutral"
                variant="leftIcon"
                iconProps={{
                  name: 'arrowReload',
                }}
                onClick={() => setAudience({ file: null, status: 'EMPTY' })}
              >
                다시 등록하기
              </Button>
            ) : null
          }
        >
          {audience.status === 'EMPTY' ? (
            <>
              <input
                type="file"
                hidden
                ref={inputRef}
                onChange={e =>
                  setAudience({ file: e.target.files?.[0] ?? null, status: 'SELECTED' })
                }
                accept="image/*"
              />
              <Button
                kind="outline"
                size="large"
                color="neutral"
                variant="leftIcon"
                iconProps={{
                  name: 'cloudUpload',
                }}
                width="100%"
                onClick={() => inputRef.current?.click()}
              >
                크리에이터 스튜디오 캡쳐본 등록
              </Button>
            </>
          ) : (
            <AudienceImageStatus
              channelSettings={channelSettings?.audienceRequest?.status || null}
              file={audience.file}
            />
          )}
        </ManageChannelInfoItem>
      </Flex>
      <Button size="large" kind="solid" color="primary" disabled={!isChanged} onClick={onClickSave}>
        저장하기
      </Button>
    </Flex>
  )
}

export default ManageChannelInfo

const CreatorPersonalInfoWrapper = styled(Flex)`
  width: 100%;

  > div {
    width: 100%;
  }
`
